import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
// Import PDF viewer components
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";

// PDF.js worker URL - using the exact version that matches the API (3.11.174)
const PDF_WORKER_URL = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

/**
 * PDF Viewer Page Component
 * 
 * A full-page PDF viewer that displays PDF documents using @react-pdf-viewer.
 * Retrieves PDF URL from URL query parameters and renders it with enhanced features.
 * 
 * @component
 * @returns {JSX.Element} The PDF viewer page component
 * 
 * @example
 * // To use this component, navigate to:
 * /pdf-viewer?url=https://example.com/document.pdf
 * 
 * @description
 * This component provides:
 * - Full-featured PDF viewing experience with toolbar
 * - Loading states while the PDF is being loaded
 * - Error handling for missing or invalid PDF URLs
 * - Responsive layout with a back button
 * - Support for zooming, searching, and navigation
 * - Print and download capabilities
 */
const PdfViewerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  
  // Initialize plugins
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      defaultTabs[0], // Thumbnails
      defaultTabs[1], // Outline
      defaultTabs[2], // Attachments
    ],
  });
  
  const searchPluginInstance = searchPlugin();
  
  // Get the PDF URL from query parameters
  const pdfUrl = searchParams.get('url');

  /**
   * Effect to handle PDF URL validation
   */
  useEffect(() => {
    if (!pdfUrl) {
      setError("No PDF URL provided. Please provide a valid URL parameter.");
      setIsLoading(false);
      return;
    }

    // If we have a URL, we can proceed
    setIsLoading(false);
  }, [pdfUrl]);

  /**
   * Handle back button click
   */
  const handleBack = () => {
    navigate(-1);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6" gutterBottom>
          Error Loading PDF
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // Main component render
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header with back button */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ marginRight: 2 }}
          >
            Back
          </Button>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PDF Viewer
          </Typography>
        </Toolbar>
      </AppBar>

      {/* PDF Viewer */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#525659",
          height: "calc(100vh - 64px)", // Adjust based on your header height
        }}
      >
        {pdfUrl ? (
          <Worker workerUrl={PDF_WORKER_URL}>
            <Viewer
              fileUrl={pdfUrl}
              plugins={[defaultLayoutPluginInstance, searchPluginInstance]}
              renderError={(error) => (
                <Box p={3} textAlign="center">
                  <Typography color="error" variant="h6" gutterBottom>
                    Error Loading PDF
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {error.message || "Unable to load the PDF file."}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowBack />}
                    onClick={handleBack}
                  >
                    Go Back
                  </Button>
                </Box>
              )}
              renderLoader={(percentages) => (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress variant="determinate" value={percentages} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading PDF... {Math.round(percentages)}%
                  </Typography>
                </Box>
              )}
            />
          </Worker>
        ) : (
          <Box p={3} textAlign="center">
            <Typography color="error" variant="h6" gutterBottom>
              No PDF URL Provided
            </Typography>
            <Typography variant="body1" paragraph>
              Please provide a valid PDF URL as a query parameter.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBack />}
              onClick={handleBack}
            >
              Go Back
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PdfViewerPage;
